import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import type { DrillPanel } from '../../../types';
import {
  NarrativePanel,
  OpportunityInsightSkeleton,
  type NarrativeAgentContext,
} from '../DrillPanels';
import { DrillTile } from './DrillTile';

export function DrillOpportunitySection({
  narrative,
  loading,
  agentCtx,
}: {
  metricKey: string;
  narrative?: Extract<DrillPanel, { kind: 'narrative' }> | null;
  loading?: boolean;
  agentCtx?: NarrativeAgentContext;
}) {
  const found = narrative && !narrative.empty ? narrative : null;
  const inspector = useMemo(() => {
    if (!found) return null;
    return (
      <NarrativePanel
        sev={found.sev}
        title={found.title}
        why={found.why}
        action={found.action}
        impact={found.impact ?? undefined}
        agentCtx={agentCtx}
      />
    );
  }, [found, agentCtx]);

  if (loading && !narrative) {
    return (
      <DrillTile
        sectionId="opportunity"
        span={12}
        title={i18n.t('performance:drillUi.opportunity')}
        strip
        agent
        data-ftue="perf-drill-opportunity"
      >
        <div className="ddrill-opp-strip">
          <div className="ddrill-opp-copy">
            <OpportunityInsightSkeleton />
          </div>
        </div>
      </DrillTile>
    );
  }

  if (!found) {
    const title = i18n.t('performance:drillUi.opportunityEmpty');
    return (
      <DrillTile
        sectionId="opportunity"
        span={12}
        title={title}
        strip
        className="is-empty"
        data-ftue="perf-drill-opportunity"
      >
        <div className="ddrill-opp-strip is-empty">
          <div className="ddrill-opp-copy">
            <div className="ddrill-opp-title">{title}</div>
            <p className="ddrill-opp-why">
              {i18n.t('performance:drillUi.opportunityEmptyWhy')}
            </p>
          </div>
        </div>
      </DrillTile>
    );
  }

  return (
    <DrillTile
      sectionId="opportunity"
      span={12}
      title={found.title}
      strip
      agent
      inspector={inspector}
      data-ftue="perf-drill-opportunity"
    >
      <div className="ddrill-opp-strip">
        {found.impact ? (
          <div className="ddrill-opp-impact">
            <strong>{found.impact.value}</strong>
            <span>{found.impact.label}</span>
          </div>
        ) : null}

        <div className="ddrill-opp-copy">
          <div className="ddrill-opp-title">{found.title}</div>
          {found.why ? <p className="ddrill-opp-why">{found.why}</p> : null}
          {found.action ? (
            <p className="ddrill-opp-action">
              <span className="ddrill-opp-action-k">
                {i18n.t('performance:drillUi.opportunityNext')}
              </span>{' '}
              {found.action}
            </p>
          ) : null}
        </div>
      </div>
    </DrillTile>
  );
}
